import { Config } from "@config";
import { 
	faCaretRight as fasCaretRight
 } from "@awesome.me/kit-3777c2145e/icons/classic/solid";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { formatText } from "@ui/format";
import { logError } from "@utils/sentry-error";
import dayjs from "dayjs";
import Highcharts from "highcharts";
import HighchartsReact from "highcharts-react-official";
import type { ReactNode } from "react";
import React from "react";
import { Col, Row } from "react-bootstrap";
import Form from "react-bootstrap/Form";
import { Form as FinalForm } from "react-final-form";
import "./style.scss";
import { http, FormSelect, PromisePool } from "bm-ui-kit";
import type { CommonOptions } from "bm-ui-kit";
import { CURRENCIES } from "../data";
import { getCurrencyName } from "../domain";
import type { Currency } from "../types";

interface ChartProps {
	currency: Currency;
	onChange?: ChangedFunction;
	isEmbed?: boolean;
}

interface ChartState {
	currency: Currency;
	filter: Filter;
	dailyResults: PortfolioItem[];
}

interface ChangedFunction {
	(currency: Currency): void;
}

interface PortfolioItem {
	price: number;
	date: string;
}

interface FormFields {
	currency: Currency;
	filter: Filter;
}

type Filter = "1day" | "5days" | "1month" | "5day" | "1year" | "10year" | "max";

const FILTERS: CommonOptions<Filter> = [
	{ label: "1 nap (24 óra)", value: "1day" },
	{ label: "5 nap", value: "5day" },
	{ label: "1 hó", value: "1month" },
	{ label: "1 év", value: "1year" },
	{ label: "10 év", value: "10year" },
	{ label: "Max", value: "max" },
];

export class Chart extends React.Component<ChartProps, ChartState> {
	private readonly promisePool = new PromisePool();

	constructor(props: ChartProps) {
		super(props);
		this.state = this.getInitState();
	}

	componentDidMount() {
		this.requestData();
	}

	componentDidUpdate(props) {
		const { currency } = this.props;

		if (props.currency !== currency) {
			this.setState(
				{
					currency,
				},
				() => this.requestData(),
			);
		}
	}

	public componentWillUnmount(): void {
		this.promisePool.cancel();
	}

	protected getInitState(): ChartState {
		const { currency } = this.props;

		return {
			currency,
			filter: "1year",
			dailyResults: [],
		};
	}

	private calc(): { increase: boolean; price: number } {
		const { dailyResults } = this.state;
		const resultLength = dailyResults.length;

		const currentItem = dailyResults[resultLength - 1];
		const lastItem = dailyResults[resultLength - 2];
		const increase = currentItem?.price < lastItem?.price;

		return { increase, price: currentItem?.price };
	}

	public render(): ReactNode {
		const { onChange, isEmbed = false } = this.props;
		const { currency, dailyResults } = this.state;
		const currencyName = getCurrencyName(currency);
		const { increase, price } = this.calc();
		const series = [
			{
				name: "Árfolyam",
				color: "#fff",
				type: "area",
				data: dailyResults.map((item) => [dayjs(item.date).unix() * 1000, item.price]),
			},
		];

		const options: Highcharts.Options = {
			time: {
				useUTC: true,
				timezoneOffset: -2 * 60,
			},
			credits: {
				enabled: false,
			},
			title: {
				text: "",
			},
			legend: {
				enabled: false,
			},
			xAxis: {
				type: "datetime",
				dateTimeLabelFormats: {
					hour: "%H:%M",
					day: {
						main: "%Y.<br/>%m. %d.",
					},
					week: {
						main: "%Y.<br/>%m. %d.",
					},
					month: {
						main: "%Y. %m.",
					},
					year: {
						main: "%Y.",
					},
				},
				labels: {
					style: {
						color: "#fff",
					},
				},
			},
			yAxis: {
				allowDecimals: true,
				title: {
					text: "",
				},
				labels: {
					style: {
						color: "#fff",
					},
					formatter() {
						if (this.value === 0) {
							return "0 Ft";
						}

						return `${formatText(this.value, "0,00 ft")}`;
					},
				},
			},
			chart: {
				backgroundColor: null,
				borderColor: "#fff",
				style: {
					color: "#fff",
				},
			},
			plotOptions: {
				area: {
					fillColor: {
						linearGradient: {
							x1: 0,
							y1: 0,
							x2: 0,
							y2: 1,
						},
						stops: [
							[0, "#f3e23f"],
							[1, Highcharts.color("#f3e23f").setOpacity(0).get("rgba").toString()],
						],
					},
					marker: {
						radius: 2,
					},
					lineWidth: 1,
					states: {
						hover: {
							lineWidth: 1,
						},
					},
					threshold: null,
				},
			},
			tooltip: {
				backgroundColor: "#fff",
				borderRadius: 10,
				padding: 8,
				shared: false,
				shadow: false,
				formatter(): string {
					return `<b>${formatText(this.y, "0,00 ft")}</b> <small>(${dayjs(this.x).format(
						"YY-MM-DD HH:mm",
					)})</small>`;
				},
				useHTML: true,
			},
			series: series as Highcharts.SeriesAreaOptions[],
		};

		// a chart-ot nem kell pererenderelni
		if (Config.isSsr()) {
			return <div className="foreign-currency-chart" />;
		}

		return (
			<div className="foreign-currency-chart">
				<FinalForm<FormFields>
					initialValuesEqual={this.isEqual}
					initialValues={this.initValues()}
					onSubmit={() => {}}
				>
					{({ handleSubmit }) => (
						<Form className="text-white" onSubmit={handleSubmit}>
							<Row>
								<Col className="col-auto currency">
									<div className="display-1 text-gold font-weight-black text-uppercase m-0">
										{currency}
									</div>
									<FontAwesomeIcon
										icon={fasCaretRight}
										rotation={increase ? 90 : 270}
										className={`mr-1 text-${increase ? "danger" : "success"}`}
									/>{" "}
									{formatText(price, "0,00 ft")}
								</Col>
								<Col>
									<div className="display-4 text-uppercase font-weight-bold">Bankközi árfolyam:</div>
									<Row className="display-5 text-uppercase" noGutters>
										{!isEmbed && (
											<Col className="col-auto">
												<FormSelect
													name="currency"
													options={CURRENCIES}
													className="mt-1 transparent-select"
													append={
														<FontAwesomeIcon
															icon={fasCaretRight}
															rotation={90}
															className="mr-1"
															color="#fff"
														/>
													}
													required
													onChange={(currentCurrency) => {
														if (onchange !== undefined) onChange(currentCurrency);
													}}
												/>
											</Col>
										)}
										<Col className="mt-1 pt-1 text-capitalize d-none d-lg-block">
											{currencyName}/Forint
										</Col>
										{isEmbed && (
											<Col className="col-auto">
												<FormSelect
													name="filter"
													options={FILTERS}
													className="mr-4 float-right"
													size="sm"
													required
													onChange={(filter) => {
														this.setState(
															{
																filter,
															},
															() => this.requestData(),
														);
													}}
												/>
											</Col>
										)}
									</Row>
								</Col>
							</Row>
							{!isEmbed && (
								<Row>
									<FormSelect
										name="filter"
										options={FILTERS}
										className="mr-4 float-right"
										size="sm"
										required
										onChange={(filter) => {
											this.setState(
												{
													filter,
												},
												() => this.requestData(),
											);
										}}
									/>
								</Row>
							)}
							<HighchartsReact highcharts={Highcharts} options={options} />
							<div className={`small mt-3${isEmbed ? "" : " mb-5"}`}>
								A grafikon a bankközi piacon az {currencyName}/forint aktuális árfolyamát mutatja.
							</div>
						</Form>
					)}
				</FinalForm>
			</div>
		);
	}

	private requestData(): void {
		const { currency, filter } = this.state;
		this.promisePool
			.add(
				http.get(
					`/wp-json/bankmonitor/v1/exchange-rate/get-current-value/?currency=${currency}&filter=${filter}`,
				),
			)
			.then((results: PortfolioItem[]) => {
				this.setState({
					dailyResults: results,
				});
			})
			.catch((error: Error) => {
				// TODO: Hibakezelés
				logError(error);
			});
	}

	protected initValues(): FormFields {
		const { currency, filter } = this.state;

		return {
			currency,
			filter,
		};
	}
	protected isEqual(a: FormFields, b: FormFields): boolean {
		return JSON.stringify(a) === JSON.stringify(b);
	}
}
